import { useCallback, useMemo, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

const UTILITY = 'regex-tester'
const DEFAULT_TEXT = 'order-1024 paid\norder-1025 pending\norder-1026 paid'
const DEFAULT_PATTERN = 'order-(\\d+)\\s+(paid|pending)'
const DEFAULT_FLAGS = 'gi'
const MAX_MATCHES = 500

interface RegexGroup {
  label: string
  value: string
}

interface RegexMatch {
  id: string
  index: number
  endIndex: number
  value: string
  groups: RegexGroup[]
}

type RegexResult =
  | {
      ok: true
      matches: RegexMatch[]
      elapsedMs: number
      truncated: boolean
    }
  | {
      ok: false
      error: string
    }

function createSearchRegex(pattern: string, flags: string): RegExp {
  const requestedFlags = flags.trim()
  new RegExp(pattern, requestedFlags)
  const scanFlags = requestedFlags.includes('g') ? requestedFlags : `${requestedFlags}g`
  return new RegExp(pattern, scanFlags.replace('y', ''))
}

function collectGroups(match: RegExpExecArray): RegexGroup[] {
  const numbered = match.slice(1).map((value, index) => ({
    label: String(index + 1),
    value: value ?? '',
  }))
  const named = Object.entries(match.groups ?? {}).map(([label, value]) => ({
    label,
    value: value ?? '',
  }))
  return numbered.concat(named)
}

function testRegex(pattern: string, flags: string, text: string): RegexResult {
  if (!pattern.trim()) {
    return { ok: false, error: 'empty-pattern' }
  }

  const start = performance.now()
  try {
    const regex = createSearchRegex(pattern, flags.trim())
    const matches: RegexMatch[] = []
    let match: RegExpExecArray | null

    while ((match = regex.exec(text)) !== null) {
      const value = match[0] ?? ''
      const index = match.index
      matches.push({
        id: `${index}-${regex.lastIndex}-${matches.length}`,
        index,
        endIndex: index + value.length,
        value,
        groups: collectGroups(match),
      })

      if (matches.length >= MAX_MATCHES) {
        return {
          ok: true,
          matches,
          elapsedMs: performance.now() - start,
          truncated: true,
        }
      }

      if (value.length === 0) {
        regex.lastIndex += 1
      }
    }

    return {
      ok: true,
      matches,
      elapsedMs: performance.now() - start,
      truncated: false,
    }
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

function buildSegments(text: string, matches: RegexMatch[]) {
  const segments: Array<
    | { type: 'text'; id: string; value: string }
    | { type: 'match'; id: string; value: string; index: number }
  > = []
  let cursor = 0

  matches.forEach((match, index) => {
    if (match.index > cursor) {
      segments.push({
        type: 'text',
        id: `text-${cursor}-${match.index}`,
        value: text.slice(cursor, match.index),
      })
    }

    segments.push({
      type: 'match',
      id: match.id,
      value: match.value,
      index,
    })
    cursor = Math.max(cursor, match.endIndex)
  })

  if (cursor < text.length) {
    segments.push({
      type: 'text',
      id: `text-${cursor}-end`,
      value: text.slice(cursor),
    })
  }

  return segments
}

function createShareUrl(pattern: string, flags: string, text: string): string {
  const url = new URL(window.location.href)
  url.pathname = '/regex-tester'
  url.search = ''
  url.searchParams.set('pattern', pattern)
  url.searchParams.set('flags', flags)
  url.searchParams.set('text', text)
  return url.toString()
}

function initialValue(key: 'pattern' | 'flags' | 'text', fallback: string): string {
  if (typeof window === 'undefined') return fallback
  return new URLSearchParams(window.location.search).get(key) ?? fallback
}

export function RegexTesterPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [pattern, setPattern] = useState(() => initialValue('pattern', DEFAULT_PATTERN))
  const [flags, setFlags] = useState(() => initialValue('flags', DEFAULT_FLAGS))
  const [text, setText] = useState(() => initialValue('text', DEFAULT_TEXT))
  const [result, setResult] = useState<RegexResult>(() =>
    testRegex(initialValue('pattern', DEFAULT_PATTERN), initialValue('flags', DEFAULT_FLAGS), initialValue('text', DEFAULT_TEXT)),
  )
  const [copied, setCopied] = useState<'summary' | 'share' | null>(null)

  const matches = result.ok ? result.matches : []
  const segments = useMemo(() => buildSegments(text, matches), [matches, text])
  const groupCount = useMemo(
    () => matches.reduce((total, match) => total + match.groups.length, 0),
    [matches],
  )

  const handleRun = useCallback(() => {
    const start = performance.now()
    toolInvoke(UTILITY, { flags: flags.trim(), text_length: text.length })
    const nextResult = testRegex(pattern, flags, text)
    setResult(nextResult)

    if (nextResult.ok) {
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        match_count: nextResult.matches.length,
        group_count: nextResult.matches.reduce((total, match) => total + match.groups.length, 0),
        flags: flags.trim(),
        truncated: nextResult.truncated,
      })
    } else {
      toolError(UTILITY, nextResult.error, {
        flags: flags.trim(),
        text_length: text.length,
      })
    }
  }, [flags, pattern, text])

  const handleClear = useCallback(() => {
    setPattern('')
    setFlags('')
    setText('')
    setResult({ ok: true, matches: [], elapsedMs: 0, truncated: false })
    setCopied(null)
  }, [])

  const handleCopySummary = useCallback(async () => {
    if (!result.ok) return
    const summary = [
      `${t.regexTester.matches}: ${matches.length}`,
      `${t.regexTester.groups}: ${groupCount}`,
      '',
      ...matches.map((match, index) => {
        const groups = match.groups
          .map((group) => `${group.label}=${group.value || t.regexTester.emptyGroup}`)
          .join(', ')
        return `#${index + 1} [${match.index}-${match.endIndex}]: ${match.value}${groups ? ` (${groups})` : ''}`
      }),
    ].join('\n')
    try {
      await navigator.clipboard.writeText(summary)
      setCopied('summary')
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }, [groupCount, matches, result.ok, t.regexTester.emptyGroup, t.regexTester.groups, t.regexTester.matches])

  const handleCopyShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(createShareUrl(pattern, flags, text))
      setCopied('share')
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }, [flags, pattern, text])

  return (
    <ToolPageShell toolKey="regexTester" width="wide">
      <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card className="overflow-hidden shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
              <div className="text-sm font-medium tracking-normal">
                {t.regexTester.patternTitle}
              </div>
              <Button variant="outline" size="sm" onClick={handleClear} className={!pattern && !flags && !text ? 'hidden' : undefined}>
                {t.regexTester.clear}
              </Button>
            </div>
            <div className="grid gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_140px]">
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {t.regexTester.pattern}
                </span>
                <input
                  value={pattern}
                  onChange={(event) => setPattern(event.target.value)}
                  placeholder={t.regexTester.patternPlaceholder}
                  className={cn(
                    'h-10 w-full rounded-lg border bg-card px-3 font-mono text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-primary/35',
                  )}
                  spellCheck={false}
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  {t.regexTester.flags}
                </span>
                <input
                  value={flags}
                  onChange={(event) => setFlags(event.target.value)}
                  placeholder={t.regexTester.flagsPlaceholder}
                  className={cn(
                    'h-10 w-full rounded-lg border bg-card px-3 font-mono text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-primary/35',
                  )}
                  spellCheck={false}
                />
              </label>
            </div>
            <div className="border-t px-4 py-3">
              <Button onClick={handleRun} disabled={!pattern.trim()}>
                {t.regexTester.run}
              </Button>
            </div>
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <div className="border-b bg-muted/45 px-4 py-3 text-sm font-medium tracking-normal">
              {t.regexTester.testText}
            </div>
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={t.regexTester.textPlaceholder}
              className={cn(
                'min-h-[360px] w-full resize-y border-0 p-5 lg:min-h-[460px]',
                'bg-card font-mono text-[14px] leading-6 text-foreground',
                'placeholder:text-muted-foreground/55',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
              )}
              aria-label={t.regexTester.testText}
              spellCheck={false}
            />
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
              <div className="text-sm font-medium tracking-normal">
                {t.regexTester.highlightedText}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleCopySummary} disabled={!result.ok || matches.length === 0}>
                  {copied === 'summary' ? t.regexTester.copied : t.regexTester.copySummary}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCopyShare}>
                  {copied === 'share' ? t.regexTester.copied : t.regexTester.copyShare}
                </Button>
              </div>
            </div>

            {!result.ok && (
              <div className="border-b bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {result.error === 'empty-pattern'
                  ? t.regexTester.errors.emptyPattern
                  : t.regexTester.errors.invalid.replace('{error}', result.error)}
              </div>
            )}

            <pre
              className={cn(
                'min-h-[300px] whitespace-pre-wrap break-words p-5 font-mono text-[14px] leading-6 lg:min-h-[380px]',
                'bg-card text-foreground',
              )}
            >
              {segments.length === 0 && (
                <span className="text-muted-foreground">{t.regexTester.noText}</span>
              )}
              {segments.map((segment) => {
                if (segment.type === 'text') {
                  return <span key={segment.id}>{segment.value}</span>
                }

                return (
                  <mark
                    key={segment.id}
                    className="rounded bg-amber-200 px-0.5 text-amber-950 ring-1 ring-amber-300 dark:bg-amber-500/35 dark:text-amber-50 dark:ring-amber-400/45"
                    title={`${t.regexTester.match} #${segment.index + 1}`}
                  >
                    {segment.value || ' '}
                  </mark>
                )
              })}
            </pre>
          </Card>
        </div>

        <aside className="space-y-6">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">
                {t.regexTester.statsTitle}
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border bg-background p-3">
                  <dt className="text-xs text-muted-foreground">{t.regexTester.matches}</dt>
                  <dd className="mt-1 text-2xl font-semibold tabular-nums">
                    {matches.length.toLocaleString(localeCode)}
                  </dd>
                </div>
                <div className="rounded-lg border bg-background p-3">
                  <dt className="text-xs text-muted-foreground">{t.regexTester.groups}</dt>
                  <dd className="mt-1 text-2xl font-semibold tabular-nums">
                    {groupCount.toLocaleString(localeCode)}
                  </dd>
                </div>
              </dl>
              {result.ok && result.truncated && (
                <p className="mt-3 text-xs leading-5 text-amber-700 dark:text-amber-200">
                  {t.regexTester.truncated.replace('{limit}', MAX_MATCHES.toLocaleString(localeCode))}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="overflow-hidden shadow-sm">
            <div className="border-b bg-muted/45 px-4 py-3 text-sm font-medium tracking-normal">
              {t.regexTester.groupsTitle}
            </div>
            <div className="max-h-[520px] overflow-auto p-4">
              {matches.length === 0 && (
                <p className="text-sm leading-6 text-muted-foreground">
                  {result.ok ? t.regexTester.noMatches : t.regexTester.fixRegex}
                </p>
              )}
              <div className="space-y-3">
                {matches.map((match, index) => (
                  <div key={match.id} className="rounded-lg border bg-background p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-mono text-sm font-semibold">
                        #{index + 1}: {match.value || t.regexTester.emptyMatch}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {match.index}-{match.endIndex}
                      </div>
                    </div>
                    {match.groups.length > 0 ? (
                      <dl className="mt-3 space-y-2">
                        {match.groups.map((group) => (
                          <div key={`${match.id}-${group.label}`} className="grid grid-cols-[72px_minmax(0,1fr)] gap-2 text-xs">
                            <dt className="font-mono text-muted-foreground">{group.label}</dt>
                            <dd className="break-words font-mono">
                              {group.value || <span className="text-muted-foreground">{t.regexTester.emptyGroup}</span>}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p className="mt-3 text-xs text-muted-foreground">
                        {t.regexTester.noGroups}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </aside>
      </section>
    </ToolPageShell>
  )
}
